import { useCallback, useEffect, useState } from "react";

// Theme preference: "system" follows the OS, "light"/"dark" override it.
// The *effective* theme is always concrete ("light" | "dark") and is what
// the rest of the app reads (and what's mirrored onto <html data-theme=…>
// for CSS to key off of).
export type ThemePref = "light" | "dark" | "system";
export type EffectiveTheme = "light" | "dark";

const STORAGE_KEY = "link.theme";

function readStoredPref(): ThemePref {
  if (typeof window === "undefined") return "system";
  const v = window.localStorage?.getItem(STORAGE_KEY);
  return v === "light" || v === "dark" || v === "system" ? v : "system";
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches === true
  );
}

export function resolveEffective(pref: ThemePref): EffectiveTheme {
  if (pref === "light" || pref === "dark") return pref;
  return systemPrefersDark() ? "dark" : "light";
}

// Mirrors the resolved theme onto <html> so CSS rules keyed off
// `html[data-theme="dark"]` apply, and the browser's own form-control /
// scrollbar chrome (driven by `color-scheme`) also matches.
export function applyTheme(effective: EffectiveTheme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.dataset.theme = effective;
  root.style.colorScheme = effective;
}

export function useTheme() {
  const [pref, setPrefState] = useState<ThemePref>(() => readStoredPref());
  const [effective, setEffective] = useState<EffectiveTheme>(() =>
    resolveEffective(readStoredPref()),
  );

  // Apply effective theme to <html> whenever it changes.
  useEffect(() => {
    applyTheme(effective);
  }, [effective]);

  // When pref changes, persist + recompute effective.
  useEffect(() => {
    try {
      window.localStorage?.setItem(STORAGE_KEY, pref);
    } catch {
      // ignore storage errors (private mode, etc.)
    }
    setEffective(resolveEffective(pref));
  }, [pref]);

  // If pref is "system", react to OS-level changes.
  useEffect(() => {
    if (pref !== "system") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setEffective(mql.matches ? "dark" : "light");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [pref]);

  const setPref = useCallback((p: ThemePref) => setPrefState(p), []);
  const cyclePref = useCallback(() => {
    setPrefState((cur) =>
      cur === "light" ? "dark" : cur === "dark" ? "system" : "light",
    );
  }, []);

  return { pref, effective, setPref, cyclePref };
}
